import { useEventListener } from "@vueuse/core";
import { ref } from "vue";

/**
 * Handles pasting a passcode over the group. A string that fills every box is distributed across
 * them; any other paste empties the boxes and raises `pasteFailed` until the next key press. The
 * box that received the paste loses focus.
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.group - Element holding the boxes.
 * @param {import("vue").ComputedRef<string>} options.value - The joined digits.
 * @param {(candidate: *) => string} options.parseValue - Returns a candidate that fits, otherwise an empty string.
 * @param {(candidate: *) => void} options.writeUserValue - Writes the digits as a user edit.
 * @param {() => void} options.reportUserEdit - Reports an edit the user made.
 * @returns {{ pasteFailed: import("vue").Ref<boolean> }}
 */
export function useOtpPaste({ group, value, parseValue, writeUserValue, reportUserEdit }) {
  const pasteFailed = ref(false);

  function clearPasteFailure() {
    pasteFailed.value = false;
  }

  function handlePaste(event) {
    event.preventDefault();

    const accepted = parseValue(event.clipboardData?.getData("text") ?? "");

    pasteFailed.value = accepted === "";

    if (accepted !== value.value) {
      writeUserValue(accepted);
      reportUserEdit();
    }

    event.target.blur();
  }

  useEventListener(group, "keydown", clearPasteFailure);
  useEventListener(group, "paste", handlePaste);

  return { pasteFailed };
}
